<template>
  <div class="chat-container">
    <!-- 左侧：国家和客户列表 -->
    <div class="chat-sidebar">
      <div class="sidebar-header">
        <h3>聊天列表</h3>
        <Button size="small" type="primary" icon="md-people" @click="showGroupModal = true">创建群聊</Button>
      </div>
      
      <!-- 国家标签 -->
      <div class="country-tabs">
        <div 
          v-for="country in countries" 
          :key="country"
          :class="['country-tab', selectedCountry === country ? 'active' : '']"
          @click="selectCountry(country)"
        >
          {{ country }}
          <span v-if="getCountryUnread(country) > 0" class="unread-badge">{{ getCountryUnread(country) }}</span>
        </div>
      </div>

      <!-- 对话列表 -->
      <div class="conversation-list">
        <div 
          v-for="conv in filteredConversations" 
          :key="conv.id"
          :class="['conversation-item', selectedConversation && selectedConversation.id === conv.id ? 'active' : '']"
          @click="selectConversation(conv)"
        >
          <div class="conv-avatar">
            <Icon :type="conv.conversation_type === 'group' ? 'md-people' : 'md-person'" size="24" />
          </div>
          <div class="conversation-info">
            <div class="conversation-name">
              {{ conv.name || conv.customer_no || '未命名' }}
              <Icon v-if="conv.conversation_type === 'group'" type="md-people" size="12" color="#999" />
            </div>
            <div class="conversation-last-message">{{ conv.last_message ? truncate(conv.last_message.content, 20) : '暂无消息' }}</div>
          </div>
          <div class="conversation-meta">
            <div class="conversation-time">{{ formatTime(conv.last_message ? conv.last_message.created_at : null) }}</div>
            <span v-if="conv.unread_count > 0" class="unread-dot">{{ conv.unread_count }}</span>
          </div>
        </div>
        <div v-if="filteredConversations.length === 0" class="empty-list">
          <p>暂无对话</p>
        </div>
      </div>
    </div>

    <!-- 右侧：聊天窗口 -->
    <div class="chat-main">
      <div v-if="!selectedConversation" class="no-conversation">
        <Icon type="ios-chatbubbles-outline" size="80" color="#ccc" />
        <p>选择左侧对话开始聊天</p>
      </div>

      <div v-else class="chat-window">
        <!-- 聊天头部 -->
        <div class="chat-header">
          <div class="chat-header-info">
            <h3>{{ selectedConversation.name || selectedConversation.customer_no }}</h3>
            <span class="chat-header-subtitle">
              {{ selectedConversation.country || '' }}
              <template v-if="selectedConversation.conversation_type === 'group'">
                &middot; 群聊 ({{ (selectedConversation.participants || []).length }}人)
              </template>
            </span>
          </div>
          <div class="chat-header-actions">
            <div class="service-mode-switch">
              <span class="switch-label">客服模式:</span>
              <i-switch 
                v-model="aiServiceEnabled" 
                size="large"
                @on-change="toggleServiceMode"
              >
                <span slot="open">AI客服</span>
                <span slot="close">人工</span>
              </i-switch>
            </div>
            <Button 
              v-if="selectedConversation.conversation_type === 'one_on_one'" 
              size="small" 
              type="warning" 
              ghost 
              icon="md-swap"
              @click="showTransferModal = true"
              style="margin-left: 8px"
            >
              转接
            </Button>
            <Button 
              v-if="selectedConversation.conversation_type === 'group'" 
              size="small" 
              type="error" 
              ghost 
              @click="dissolveGroup"
              style="margin-left: 8px"
            >
              解散群聊
            </Button>
          </div>
        </div>

        <!-- 消息列表 -->
        <div class="message-list" ref="messageList">
          <div v-for="msg in messages" :key="msg.id" :class="['message-item', msg.sender_user_id ? 'sent' : 'received']">
            <div class="msg-avatar">
              <Icon :type="msg.sender_user_id ? 'md-person' : 'md-contact'" size="20" />
            </div>
            <div class="message-content-wrap">
              <div class="message-sender-name">{{ msg.sender_name }}</div>
              <div v-if="msg.message_type === 'image'" class="message-image">
                <img :src="getImageUrl(msg.content)" @click="previewImage(msg.content)" />
              </div>
              <div v-else class="message-bubble">
                {{ msg.content }}
                <Tag v-if="msg.is_from_bot" color="green" size="small" style="margin-left:4px">AI</Tag>
              </div>
              <div class="message-time">{{ formatTime(msg.created_at) }}</div>
            </div>
          </div>
          <div v-if="messages.length === 0" class="no-messages">
            <p>暂无消息，发送第一条消息开始聊天</p>
          </div>
        </div>

        <!-- 输入框 -->
        <div class="chat-input-area">
          <div class="input-toolbar">
            <Upload
              :action="uploadUrl"
              :headers="uploadHeaders"
              :show-upload-list="false"
              :on-success="handleImageUpload"
              :on-error="handleUploadError"
              accept="image/*"
            >
              <Icon type="md-image" size="22" class="toolbar-icon" />
            </Upload>
          </div>
          <div class="input-row">
            <Input 
              v-model="messageInput" 
              type="textarea" 
              :rows="2" 
              placeholder="输入消息，Enter发送..."
              @on-enter="sendMessage"
            />
            <Button type="primary" icon="md-send" @click="sendMessage" :loading="sending">发送</Button>
          </div>
        </div>
      </div>
    </div>

    <!-- 转接客服弹窗 -->
    <Modal v-model="showTransferModal" title="转接给其他客服" @on-ok="transferConversation" ok-text="转接" cancel-text="取消">
      <Form :label-width="80">
        <FormItem label="目标客服">
          <Select v-model="transferForm.target_user_id" filterable placeholder="选择客服">
            <Option v-for="s in staffList" :key="s.id" :value="s.id">
              {{ s.nickname || s.username }} ({{ s.position }})
            </Option>
          </Select>
        </FormItem>
        <FormItem label="转接原因">
          <Input v-model="transferForm.reason" type="textarea" :rows="2" placeholder="可选，说明转接原因" />
        </FormItem>
      </Form>
    </Modal>

    <!-- 创建群聊弹窗 -->
    <Modal v-model="showGroupModal" title="创建群聊" @on-ok="createGroup" ok-text="创建" cancel-text="取消">
      <Form :label-width="80">
        <FormItem label="群聊名称">
          <Input v-model="groupForm.name" placeholder="请输入群聊名称" />
        </FormItem>
        <FormItem label="选择客户">
          <Select v-model="groupForm.customers" multiple filterable placeholder="输入客户编号搜索">
            <Option v-for="c in allCustomers" :key="c.customer_no" :value="c.customer_no">
              {{ c.customer_no }} - {{ c.customer_name || '未命名' }}
            </Option>
          </Select>
        </FormItem>
        <FormItem label="选择员工">
          <Select v-model="groupForm.staff" multiple filterable placeholder="选择员工">
            <Option v-for="u in allStaff" :key="u.id" :value="u.id">
              {{ u.nickname || u.username }}
            </Option>
          </Select>
        </FormItem>
      </Form>
    </Modal>
  </div>
</template>

<script>
import axios from 'axios'

const API_BASE = process.env.VUE_APP_API_BASE_URL || ''

function getToken() {
  return localStorage.getItem('token')
}

function authHeaders() {
  return { Authorization: 'Bearer ' + getToken() }
}

export default {
  name: 'ChatManagement',
  data() {
    return {
      conversations: [],
      selectedCountry: '全部',
      selectedConversation: null,
      messages: [],
      messageInput: '',
      sending: false,
      ws: null,
      showGroupModal: false,
      groupForm: { name: '', customers: [], staff: [] },
      allCustomers: [],
      allStaff: [],
      uploadUrl: API_BASE + '/api/chat/upload',
      uploadHeaders: authHeaders(),
      aiServiceEnabled: false,
      showTransferModal: false,
      transferForm: { target_user_id: null, reason: '' },
      staffList: []
    }
  },
  computed: {
    countries() {
      const set = new Set(this.conversations.map(c => c.country).filter(Boolean))
      return ['全部', ...Array.from(set)]
    },
    filteredConversations() {
      if (this.selectedCountry === '全部') return this.conversations
      return this.conversations.filter(c => c.country === this.selectedCountry)
    }
  },
  mounted() {
    this.loadConversations()
    this.loadCustomers()
    this.loadStaff()
    this.loadStaffList()
  },
  beforeDestroy() {
    if (this.ws) this.ws.close()
  },
  methods: {
    async loadConversations() {
      try {
        const res = await axios.get(API_BASE + '/api/chat/conversations', { headers: authHeaders() })
        this.conversations = res.data.conversations || []
      } catch (e) {
        console.error('加载会话失败', e)
      }
    },
    async loadCustomers() {
      try {
        const res = await axios.get(API_BASE + '/api/customers', { headers: authHeaders() })
        this.allCustomers = res.data.customers || res.data || []
      } catch (e) {
        console.error('加载客户失败', e)
      }
    },
    async loadStaff() {
      try {
        const res = await axios.get(API_BASE + '/api/users', { headers: authHeaders() })
        this.allStaff = res.data.users || res.data || []
      } catch (e) {
        console.error('加载员工失败', e)
      }
    },
    selectCountry(country) {
      this.selectedCountry = country
    },
    selectConversation(conv) {
      this.selectedConversation = conv
      this.loadMessages(conv.id)
      this.loadAiMode()
      this.connectWebSocket(conv.id)
    },
    async loadMessages(conversationId) {
      try {
        const res = await axios.get(API_BASE + '/api/chat/conversations/' + conversationId + '/messages', { headers: authHeaders() })
        this.messages = res.data.messages || []
        this.$nextTick(() => this.scrollToBottom())
      } catch (e) {
        console.error('加载消息失败', e)
      }
    },
    connectWebSocket(conversationId) {
      if (this.ws) { this.ws.close(); this.ws = null }

      const token = getToken()
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      const host = window.location.host
      const wsUrl = protocol + '//' + host + '/api/chat/ws/' + conversationId + '?token=' + token

      this.ws = new WebSocket(wsUrl)

      this.ws.onmessage = (event) => {
        const msg = JSON.parse(event.data)
        this.messages.push(msg)
        this.$nextTick(() => this.scrollToBottom())
      }

      this.ws.onerror = (err) => {
        console.error('WS error:', err)
      }

      this.ws.onclose = () => {
        this.ws = null
      }
    },
    sendMessage() {
      if (!this.messageInput.trim()) return
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        this.$Message.warning('连接未就绪，请稍后重试')
        return
      }
      this.sending = true
      try {
        this.ws.send(JSON.stringify({ type: 'text', content: this.messageInput }))
        this.messageInput = ''
      } catch (e) {
        this.$Message.error('发送失败')
      } finally {
        this.sending = false
      }
    },
    handleImageUpload(response) {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        this.$Message.error('连接未就绪')
        return
      }
      this.ws.send(JSON.stringify({ type: 'image', content: response.url }))
    },
    handleUploadError() {
      this.$Message.error('图片上传失败')
    },
    async createGroup() {
      if (!this.groupForm.name) {
        this.$Message.warning('请输入群聊名称')
        return
      }
      try {
        await axios.post(API_BASE + '/api/chat/conversations/group', {
          name: this.groupForm.name,
          customer_nos: this.groupForm.customers,
          staff_user_ids: this.groupForm.staff
        }, { headers: authHeaders() })
        this.$Message.success('群聊创建成功')
        this.showGroupModal = false
        this.groupForm = { name: '', customers: [], staff: [] }
        this.loadConversations()
      } catch (e) {
        this.$Message.error('创建失败')
      }
    },
    dissolveGroup() {
      this.$Modal.confirm({
        title: '确认解散',
        content: '确定解散此群聊？所有消息将无法访问。',
        onOk: async () => {
          try {
            await axios.delete(API_BASE + '/api/chat/conversations/' + this.selectedConversation.id, { headers: authHeaders() })
            this.$Message.success('群聊已解散')
            this.selectedConversation = null
            this.messages = []
            this.loadConversations()
          } catch (e) {
            this.$Message.error('操作失败')
          }
        }
      })
    },
    getCountryUnread(country) {
      const list = country === '全部' ? this.conversations : this.conversations.filter(c => c.country === country)
      return list.reduce((sum, c) => sum + (c.unread_count || 0), 0)
    },
    getImageUrl(url) {
      if (url.startsWith('http')) return url
      return API_BASE + url
    },
    previewImage(url) {
      window.open(this.getImageUrl(url), '_blank')
    },
    truncate(str, len) {
      if (!str) return ''
      return str.length > len ? str.substring(0, len) + '...' : str
    },
    formatTime(ts) {
      if (!ts) return ''
      const date = new Date(ts)
      const now = new Date()
      const diff = now - date
      if (diff < 60000) return '刚刚'
      if (diff < 3600000) return Math.floor(diff / 60000) + '分钟前'
      if (date.toDateString() === now.toDateString()) {
        return date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
      }
      return date.toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit' }) + ' ' + date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
    },
    async loadStaffList() {
      try {
        const resp = await axios.get(API_BASE + '/api/chat/staff', { headers: authHeaders() })
        this.staffList = resp.data.staff || []
      } catch (e) { console.error('加载客服列表失败', e) }
    },
    async transferConversation() {
      if (!this.transferForm.target_user_id) {
        this.$Message.warning('请选择目标客服')
        return
      }
      try {
        await axios.post(
          API_BASE + '/api/chat/conversations/' + this.selectedConversation.id + '/transfer',
          this.transferForm,
          { headers: authHeaders() }
        )
        this.$Message.success('转接成功')
        this.showTransferModal = false
        this.transferForm = { target_user_id: null, reason: '' }
        this.loadConversations()
      } catch (e) {
        this.$Message.error('转接失败')
      }
    },
    scrollToBottom() {
      const el = this.$refs.messageList
      if (el) el.scrollTop = el.scrollHeight
    },
    async toggleServiceMode(enabled) {
      if (!this.selectedConversation) return
      
      try {
        const endpoint = API_BASE + '/api/chat/conversations/' + this.selectedConversation.id + '/ai-mode'
        await axios.put(endpoint, { ai_enabled: enabled }, { headers: authHeaders() })
        
        const mode = enabled ? 'AI客服' : '人工客服'
        this.$Message.success('已切换到' + mode + '模式')
        
        // 发送系统消息通知
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.ws.send(JSON.stringify({
            type: 'system',
            content: '系统提示：客服模式已切换为' + mode
          }))
        }
      } catch (e) {
        this.$Message.error('切换模式失败')
        this.aiServiceEnabled = !enabled
      }
    },
    async loadAiMode() {
      if (!this.selectedConversation) return
      
      try {
        const endpoint = API_BASE + '/api/chat/conversations/' + this.selectedConversation.id + '/ai-mode'
        const resp = await axios.get(endpoint, { headers: authHeaders() })
        this.aiServiceEnabled = resp.data.ai_enabled || false
      } catch (e) {
        console.error('加载AI模式失败', e)
      }
    }
  }
}
</script>

<style scoped>
.chat-container {
  display: flex;
  height: calc(100vh - 130px);
  background: #f5f5f5;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e8e8e8;
}

.chat-sidebar {
  width: 320px;
  background: #fff;
  border-right: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;
}

.sidebar-header {
  padding: 14px 16px;
  border-bottom: 1px solid #e8e8e8;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.sidebar-header h3 {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
}

.country-tabs {
  display: flex;
  padding: 8px 12px;
  gap: 6px;
  border-bottom: 1px solid #f0f0f0;
  overflow-x: auto;
  flex-wrap: nowrap;
}

.country-tab {
  padding: 4px 10px;
  border-radius: 12px;
  cursor: pointer;
  white-space: nowrap;
  font-size: 13px;
  background: #f5f5f5;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 4px;
}

.country-tab:hover { background: #e6f7ff; }
.country-tab.active { background: #1890ff; color: #fff; }

.unread-badge {
  background: #ff4d4f;
  color: #fff;
  border-radius: 10px;
  padding: 0 6px;
  font-size: 11px;
  min-width: 18px;
  text-align: center;
  line-height: 18px;
}

.conversation-list {
  flex: 1;
  overflow-y: auto;
}

.conversation-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid #f8f8f8;
  transition: background 0.2s;
}

.conversation-item:hover { background: #f9f9f9; }
.conversation-item.active { background: #e6f7ff; }

.conv-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #e8e8e8;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.conversation-info {
  flex: 1;
  margin-left: 12px;
  overflow: hidden;
}

.conversation-name {
  font-weight: 500;
  font-size: 14px;
  margin-bottom: 2px;
}

.conversation-last-message {
  font-size: 12px;
  color: #999;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.conversation-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.conversation-time {
  font-size: 11px;
  color: #bbb;
}

.unread-dot {
  background: #ff4d4f;
  color: #fff;
  border-radius: 10px;
  padding: 0 6px;
  font-size: 11px;
  min-width: 18px;
  text-align: center;
  line-height: 18px;
}

.empty-list {
  padding: 40px;
  text-align: center;
  color: #ccc;
}

.chat-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #fff;
}

.no-conversation {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #ccc;
}
.no-conversation p { margin-top: 12px; }

.chat-window {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.chat-header {
  padding: 12px 16px;
  border-bottom: 1px solid #e8e8e8;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fafafa;
}

.chat-header-info h3 { margin: 0; font-size: 15px; }
.chat-header-subtitle { font-size: 12px; color: #999; }

.message-list {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background: #f5f5f5;
}

.message-item {
  display: flex;
  margin-bottom: 16px;
  align-items: flex-start;
}

.message-item.sent { flex-direction: row-reverse; }

.msg-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #ddd;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.message-content-wrap {
  max-width: 60%;
  margin: 0 10px;
}

.message-item.sent .message-content-wrap {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.message-sender-name {
  font-size: 11px;
  color: #999;
  margin-bottom: 2px;
}

.message-bubble {
  padding: 8px 12px;
  border-radius: 8px;
  background: #fff;
  font-size: 14px;
  line-height: 1.5;
  word-break: break-word;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}

.message-item.sent .message-bubble {
  background: #1890ff;
  color: #fff;
}

.message-image img {
  max-width: 200px;
  max-height: 200px;
  border-radius: 8px;
  cursor: pointer;
}

.message-time {
  font-size: 11px;
  color: #ccc;
  margin-top: 2px;
}

.no-messages {
  text-align: center;
  color: #ccc;
  padding: 60px 0;
}

.chat-input-area {
  border-top: 1px solid #e8e8e8;
  padding: 10px 16px;
  background: #fff;
}

.input-toolbar {
  display: flex;
  gap: 12px;
  margin-bottom: 8px;
}

.toolbar-icon {
  cursor: pointer;
  color: #666;
  transition: color 0.2s;
}
.toolbar-icon:hover { color: #1890ff; }

.input-row {
  display: flex;
  gap: 8px;
  align-items: flex-end;
}

.input-row >>> .ivu-input-wrapper { flex: 1; }

.chat-header-actions {
  display: flex;
  align-items: center;
}

.service-mode-switch {
  display: flex;
  align-items: center;
  padding: 4px 12px;
  background: #f5f5f5;
  border-radius: 4px;
}

.switch-label {
  font-size: 13px;
  color: #666;
  margin-right: 8px;
}
</style>


